import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  CircularProgress,
  Grid,
  Stack,
  Typography,
  TextField,
  FormControl,
  Card,
  DialogTitle,
  DialogContent,
  Dialog,
  InputAdornment
} from '@mui/material'
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline'
import DeleteIcon from '@mui/icons-material/Delete'
import {
  ButtonWithIcon,
  EditActions,
  detailsContainerStyles,
  IconButtonWithTooltip,
  LastUpdatedComponent,
  useGlobalMessageHandler,
  ChamberSelect, UsStateSelect, PersonMiniCard, UsAddressFields, usAddressIsValid, ResponsibilityMultiSelect
  , PhoneCard, UrlCardItem, AlertDialog, PersonAutocomplete,
  PhoneFaxSection, JurisdictionCard, LeadershipOfficerDisplayOrderDialog,
  useUsPrefixLuts
} from 'components'
import { useSessionContext, LEADERSHIP_NAV_SORT_DEFAULT, type LeadershipNavSort } from 'session'
import {
  type BaseAddress,
  fillLeadership,
  getLeadershipFullInfo,
  type LeadershipOfficer,
  type LeadershipOfficerWithAddressAndUrls,
  type LeadershipOfficerDisplayOrder,
  type PersonBaseBasicInformation,
  retireLeadership,
  updateLeadershipFullInfo,
  deleteLeadership,
  type LeadershipPhone,
  type LeadershipOfficeJurisdiction, getLeadershipOfficerJurisdiction
} from 'services'
import { useNavigate, useParams } from 'react-router-dom'
import { getLeadershipOfficerNavigation } from 'services/leadership/leadershipOfficerNavigationService'
import { useRecordNavigation } from 'components/us/common/useRecordNavigation'
import { RecordNavigationButtons } from 'components/us/common/RecordNavigationButtons'
import { RecordNavigationSortMenu, type SortOption } from 'components/us/common/RecordNavigationSortMenu'
import { UnsavedChangesDialog } from 'components/us/common/UnsavedChangesDialog'
import {
  createEmptyLeadershipJurisdiction, filterBuildingsForLeadership,
  generateLegalName,
  getEmptyLeadershipPhone,
  getEmptyLeadershipUrl
} from 'helpers'
import AddIcon from '@mui/icons-material/Add'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import { formatErrorFieldMessages } from 'interfaces'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { CommentDialog } from 'components/us/common/comment-dialog'
import { US_LEADERSHIP_OFFICER_ROLES, PHONE_TYPES } from 'utils'

const LEADERSHIP_NAV_SORT_OPTIONS: SortOption[] = [
  { value: 'display_order', label: 'Display Order' },
  { value: 'last_name', label: 'Last Name' }
]

export const LeadershipInfoPage: React.FC = () => {
  const { legOfcId } = useParams<{ legOfcId: string }>()
  const parsedLegOfcId = legOfcId != null ? parseInt(legOfcId, 10) : 0
  const [leadershipOriginalInfo, setLeadershipOriginalInfo] = useState<LeadershipOfficerWithAddressAndUrls | undefined>(undefined)
  const [leadershipUpdatedInfo, setLeadershipUpdatedInfo] = useState<LeadershipOfficerWithAddressAndUrls>()
  const [leadershipJurisdiction, setLeadershipJurisdiction] = useState<LeadershipOfficeJurisdiction | undefined>(undefined)

  const [showRetireConfirmation, setShowRetireConfirmation] = useState<boolean>(false)
  const [openFillDialog, setOpenFillDialog] = useState(false)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [disabled, setDisabled] = useState<boolean>(true)
  const [loadingClick, setLoadingClick] = useState(false)
  const [displayOrderModalOpen, setDisplayOrderModalOpen] = useState(false)
  const { sessionData, openNewTab, changeModuleName, changeUniqueId, changeLeadershipNavSortBy } = useSessionContext()
  const { lutValues: usPrefixLutValues } = useUsPrefixLuts()
  const { showSuccessMessage, showErrorMessage, clearMessages } = useGlobalMessageHandler()

  const navigate = useNavigate()
  const navSortBy: LeadershipNavSort = sessionData.leadershipNavSortBy ?? LEADERSHIP_NAV_SORT_DEFAULT
  const handleNavSortChange = useCallback((next: string): void => {
    changeLeadershipNavSortBy(next as LeadershipNavSort)
  }, [changeLeadershipNavSortBy])
  const fetchLeadershipNav = useCallback(
    async (id: number) => await getLeadershipOfficerNavigation(id, navSortBy),
    [navSortBy]
  )
  const { previousId, nextId, loading: navLoading } = useRecordNavigation(
    fetchLeadershipNav,
    parsedLegOfcId
  )

  const [pendingNavigationId, setPendingNavigationId] = useState<number | null>(null)
  const [unsavedChangesOpen, setUnsavedChangesOpen] = useState(false)

  // Edit mode (`!disabled`) is the dirty signal — same heuristic the page already
  // uses elsewhere; navigating away while editing prompts for confirmation.
  const isDirty = !disabled

  const handleNavigateToRecord = (id: number): void => {
    if (isDirty) {
      setPendingNavigationId(id)
      setUnsavedChangesOpen(true)
      return
    }
    navigate(`/us/leadershipOfficers/edit/${id}`)
  }

  const handleSaveAndNavigate = (): void => {
    setUnsavedChangesOpen(false)
    if (leadershipUpdatedInfo == null) return
    clearMessages()
    if (!usAddressIsValid(leadershipUpdatedInfo.address, showErrorMessage)) return
    setLoadingClick(true)
    void updateLeadershipFullInfo(parsedLegOfcId, leadershipUpdatedInfo)
      .then((value) => {
        if (value.succeeded && value.result != null) {
          showSuccessMessage('Leadership/Officer Updated')
          setLeadershipOriginalInfo(value.result)
          setDisabled(true)
          if (pendingNavigationId != null) {
            const target = pendingNavigationId
            setPendingNavigationId(null)
            navigate(`/us/leadershipOfficers/edit/${target}`)
          }
        } else {
          showErrorMessage(formatErrorFieldMessages(value.errors))
          setPendingNavigationId(null)
        }
      })
      .finally(() => {
        setLoadingClick(false)
      })
  }

  const handleDiscardAndNavigate = (): void => {
    setUnsavedChangesOpen(false)
    setDisabled(true)
    setLeadershipUpdatedInfo(leadershipOriginalInfo)
    if (pendingNavigationId != null) {
      const target = pendingNavigationId
      setPendingNavigationId(null)
      navigate(`/us/leadershipOfficers/edit/${target}`)
    }
  }

  const handleCancelNavigation = (): void => {
    setUnsavedChangesOpen(false)
    setPendingNavigationId(null)
  }

  useEffect(() => {
    const generateModuleName = (leadership: LeadershipOfficer | null | undefined): string => {
      if (leadership == null || leadership.person.id == null || leadership.state?.desc == null) return 'Leadership/Officer Staffers'
      return `${leadership.ofcname} (${leadership.legchamber.desc}${leadership.state.desc !== 'All'
          ? `, ${leadership.state.desc}`
          : ', U.S'
      }): ${generateLegalName(leadership.person as PersonBaseBasicInformation, usPrefixLutValues.prefixes)}`
    }

    changeModuleName(generateModuleName(leadershipOriginalInfo))
    changeUniqueId(`Leadership Officer (id:${parsedLegOfcId})`)
  }, [changeModuleName, leadershipOriginalInfo, usPrefixLutValues.prefixes])

  const setProperty = (property: string, value: any): void => {
    setLeadershipUpdatedInfo(prev => {
      if (prev == null) return undefined

      if (property === 'address') {
        return { ...prev, [property]: { ...value, phones: prev.address?.phones ?? [] } }
      }

      if (property === 'phones') {
        return {
          ...prev,
          address: {
            ...prev.address,
            phones: value
          }
        }
      }

      return { ...prev, [property]: value }
    })
  }

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' })
  }, [parsedLegOfcId])

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setLoadingClick(true)
        const [leadership, leadershipJurisdiction] = await Promise.all([getLeadershipFullInfo(parsedLegOfcId), getLeadershipOfficerJurisdiction(parsedLegOfcId)])
        if (leadership.succeeded && leadership.result !== null) {
          const result = leadership.result

          setLeadershipOriginalInfo(result)
          setLeadershipUpdatedInfo(result)
        }
        if (leadershipJurisdiction.succeeded && leadershipJurisdiction.result !== null) {
          setLeadershipJurisdiction(leadershipJurisdiction.result)
        } else {
          if (leadershipJurisdiction.status === 404) { setLeadershipJurisdiction(createEmptyLeadershipJurisdiction(parsedLegOfcId)) }
        }
      } catch (error: any) {
        showErrorMessage('An unexpected error occurred during the request.')
      } finally {
        setLoadingClick(false)
      }
    }
    void fetchData()
  }, [parsedLegOfcId])

  const handleRetire = (): void => {
    const personId = leadershipUpdatedInfo?.person.id
    if (personId == null) return
    clearMessages()
    setLoadingClick(true)
    void retireLeadership(parsedLegOfcId, personId).then((value) => {
      if (value.succeeded && value.result != null) {
        showSuccessMessage('Leadership/Officer Updated')
        setLeadershipOriginalInfo(value.result)
        setLeadershipUpdatedInfo(value.result)
        setDisabled(true)
      } else {
        showErrorMessage(formatErrorFieldMessages(value.errors))
      }
    }).finally(() => {
      setLoadingClick(false)
    })
  }

  const handleFill = (person: PersonBaseBasicInformation): void => {
    if (leadershipUpdatedInfo == null) return
    clearMessages()
    setLoadingClick(true)
    setOpenFillDialog(false)

    const fillPromise = fillLeadership(
      parsedLegOfcId,
      {
        personId: person.id,
        firstName: person.firstName,
        lastName: person.lastName
      }
    )

    void fillPromise.then((value) => {
      if (value.succeeded && value.result != null) {
        showSuccessMessage('Leadership/Officer Updated')
        setLeadershipOriginalInfo(value.result)
        setLeadershipUpdatedInfo(value.result)
        setDisabled(true)
      } else {
        showErrorMessage(formatErrorFieldMessages(value.errors))
      }
    }).finally(() => {
      setLoadingClick(false)
    })
  }

  const handlePostClick = (): void => {
    if (leadershipUpdatedInfo == null) return
    clearMessages()
    setLoadingClick(true)
    void updateLeadershipFullInfo(parsedLegOfcId, leadershipUpdatedInfo).then((value) => {
      if (value.succeeded && value.result != null) {
        showSuccessMessage('Leadership/Officer Updated')
        setLeadershipOriginalInfo(value.result)
        setDisabled(true)
      } else {
        showErrorMessage(formatErrorFieldMessages(value.errors))
      }
    }).finally(() => {
      setLoadingClick(false)
    })
  }
  if (leadershipOriginalInfo?.id == null || leadershipUpdatedInfo?.id == null) return <Box display="flex" height="90vh" alignItems="center" justifyContent="center"><CircularProgress/></Box>
  const filledPerson = leadershipUpdatedInfo.person.lastName !== 'Vacant'

  const getLeadershipCommentRecordType = (leadershipRoleId: number): string => {
    switch (leadershipRoleId) {
      case US_LEADERSHIP_OFFICER_ROLES.FEDERAL_OFFICER:
      case US_LEADERSHIP_OFFICER_ROLES.STATE_OFFICER:
        return 'O'
      case US_LEADERSHIP_OFFICER_ROLES.FEDERAL_LEADERSHIP:
      case US_LEADERSHIP_OFFICER_ROLES.STATE_LEADERSHIP:
        return 'L'
      default:
        throw new Error(`Invalid leadership role ID: ${leadershipRoleId}`)
    }
  }

  // Add this function to ensure there's at least one phone and URL when editing starts
  const handleStartEditing = (): void => {
    setDisabled(false)
    setLeadershipUpdatedInfo(prev => {
      if (prev == null) return undefined
      return {
        ...prev,
        address: {
          ...prev.address,
          phones: prev.address.phones?.length > 0
            ? prev.address.phones
            : [getEmptyLeadershipPhone(prev.id, prev.address.id)]
        },
        urls: prev.urls?.length > 0
          ? prev.urls
          : [getEmptyLeadershipUrl(prev.id, 1, false)],
        responsibilities: prev?.responsibilities ?? []
      }
    })
  }

  const handleDelete = (): void => {
    if (leadershipUpdatedInfo == null) return
    clearMessages()
    setLoadingClick(true)
    setOpenDeleteDialog(false)

    deleteLeadership(parsedLegOfcId).then((value) => {
      if (value.succeeded) {
        showSuccessMessage('Leadership/Officer Deleted')
        window.location.href = '/us/leadershipOfficers'
      } else {
        showErrorMessage(formatErrorFieldMessages(value.errors))
      }
    }).finally(() => {
      setLoadingClick(false)
    })
  }

  const handleDisplayOrderModalOpen = (): void => {
    setDisplayOrderModalOpen(true)
  }

  const handleDisplayOrderModalClose = (): void => {
    setDisplayOrderModalOpen(false)
  }

  const handleDisplayOrderUpdated = async (updatedDisplayOrder: LeadershipOfficerDisplayOrder[]): Promise<void> => {
    // Update the current leadership officer's display order from the returned data
    const currentOfficer = updatedDisplayOrder.find(officer => officer.id === parsedLegOfcId)
    if (currentOfficer != null) {
      const updatedInfo = {
        ...leadershipUpdatedInfo,
        display_order: currentOfficer.displayOrder
      }
      setLeadershipUpdatedInfo(updatedInfo)
      setLeadershipOriginalInfo(updatedInfo)
    }
  }

  return (
    <Stack>
      <Box sx={{
        mt: 4,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%'
      }}>
        <Box sx={{ width: '98%', display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <RecordNavigationButtons
            previousId={previousId}
            nextId={nextId}
            onNavigate={handleNavigateToRecord}
            loading={loadingClick || navLoading}
          />
          <RecordNavigationSortMenu
            options={LEADERSHIP_NAV_SORT_OPTIONS}
            value={navSortBy}
            onChange={handleNavSortChange}
            disabled={loadingClick || navLoading}
          />
        </Box>
        <Card sx={{ width: '98%', padding: 1 }} >
        <Grid container spacing={2} mt={1}>
          <Grid item xs={10}/>
          <Grid item xs={2}>
          <LastUpdatedComponent value={leadershipUpdatedInfo?.lastUpdated}></LastUpdatedComponent>
          </Grid>
          <Grid item xs={1.5} md={1.2}>
            <Box display="flex" flexDirection="column">
              <Box mt={4}>
                <Stack spacing={3}>
                  <ButtonWithIcon fullWidth={true} nameButton={'Staffers'} icon={<PeopleOutlineIcon />} onClick={function (): void {
                    openNewTab(`/us/leadershipOfficers/staff/${parsedLegOfcId}`)
                  } } />
                </Stack>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={10.5} sx={detailsContainerStyles}>
            <Stack spacing={1}>
              <Grid container spacing={2}>
                <Grid item xs={10}>
                  <TextField
                    id="leaderName"
                    disabled={disabled}
                    label="Leader Name"
                    variant="outlined"
                    value={leadershipUpdatedInfo.ofcname}
                    onChange={(e) => {
                      setProperty('ofcname', e.target.value)
                    }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    id="displayOrder"
                    disabled={disabled}
                    label="Display Order"
                    type="number"
                    variant="outlined"
                    value={leadershipUpdatedInfo.display_order}
                    fullWidth
                    inputProps={{
                      readOnly: true,
                      style: {
                        MozAppearance: 'textfield'
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButtonWithTooltip
                            color="primary"
                            label="Edit Officer Display Order Information"
                            onClick={handleDisplayOrderModalOpen}
                            disabled={disabled}
                            icon={<OpenInNewIcon />}
                          />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <FormControl variant="outlined" fullWidth>
                    <UsStateSelect
                      value={leadershipUpdatedInfo.state}
                      updateState={(state) => {
                        setProperty('state', state)
                      }}
                      disabled={disabled}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={3}>
                  <FormControl variant="outlined" fullWidth>
                    <ChamberSelect
                      value={leadershipUpdatedInfo.legchamber}
                      updateChamber={(chamber) => {
                        setProperty('legchamber', chamber)
                      }}
                      disabled={disabled}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={1}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      height: '100%'
                    }}
                  >
                    { filledPerson ? 'Current Leader:' : 'Vacant'}
                  </Typography>
                </Grid>
                <Grid item xs={3} alignContent={'center'}>
                  {filledPerson &&
                  <PersonMiniCard
                    person={leadershipUpdatedInfo.person as PersonBaseBasicInformation}
                    filled
                    withPhoto
                  />
                  }
                  {!loadingClick && !filledPerson && <IconButtonWithTooltip label={'Fill Member Position'} color={'primary'} icon={<PersonAddIcon/>} onClick={() => { setOpenFillDialog(true) }} disabled={!disabled}/>}

                </Grid>
                <Grid item xs={1} alignContent={'center'}>
                  {!loadingClick && filledPerson && <IconButtonWithTooltip label={'Retire Leader'} color={'secondary'} icon={<HourglassEmptyIcon/>} onClick={() => { setShowRetireConfirmation(true) }} disabled={!disabled}/>}
                  {loadingClick && <CircularProgress/>}
                </Grid>
              </Grid>
            </Stack>
          </Grid>
          <Grid item xs={12} >
            <Box>
            <UsAddressFields
                disabled={disabled}
                startExpanded={true}
                updateAddress={(baseAddress: BaseAddress) => {
                  setProperty('address', baseAddress)
                }}
                resetAddress={() => { } }
                comAddress={{ ...leadershipUpdatedInfo.address, phones: [] }}
                showPhones={false}
                filterBuildings={(buildings) => filterBuildingsForLeadership(buildings, leadershipOriginalInfo)}
            />
              <Box pt={2}>
                <Typography variant="body2" color={'secondary.dark'} sx={{ fontWeight: 'bold' }} paddingBottom={2}>Phones:</Typography>
                <PhoneFaxSection<LeadershipPhone>
                  phone={leadershipUpdatedInfo.address.phones?.find(p => p.typeId === PHONE_TYPES.PHONE)}
                  fax={leadershipUpdatedInfo.address.phones?.find(p => p.typeId === PHONE_TYPES.FAX)}
                  disabled={disabled}
                  handlePhoneChange={(isFax, field, value) => {
                    const typeId = isFax ? PHONE_TYPES.FAX : PHONE_TYPES.PHONE
                    const phoneToUpdate = leadershipUpdatedInfo.address.phones?.find(p => p.typeId === typeId)

                    if (phoneToUpdate != null) {
                      const updatedPhone = { ...phoneToUpdate, [field]: value }
                      setProperty('phones', leadershipUpdatedInfo.address.phones.map(p =>
                        p.id === phoneToUpdate.id ? updatedPhone : p
                      ))
                    }
                  }}
                  handleAddPhone={(isPhoneType) => {
                    const typeId = isPhoneType ? PHONE_TYPES.PHONE : PHONE_TYPES.FAX
                    const newPhone = getEmptyLeadershipPhone(leadershipUpdatedInfo.id, leadershipUpdatedInfo.address.id)
                    newPhone.typeId = typeId

                    const currentPhones = leadershipUpdatedInfo.address.phones ?? []
                    setProperty('phones', [...currentPhones, newPhone])
                  }}
                  handleDeletePhone={(isPhoneType) => {
                    const typeId = isPhoneType ? PHONE_TYPES.PHONE : PHONE_TYPES.FAX
                    setProperty('phones', leadershipUpdatedInfo.address.phones.filter(p => p.typeId !== typeId))
                  }}
                />

                {leadershipUpdatedInfo.address.phones?.filter(p =>
                  p.typeId !== PHONE_TYPES.PHONE && p.typeId !== PHONE_TYPES.FAX
                ).map((phone) => (
                  <PhoneCard
                    key={phone.id}
                    phone={phone}
                    disabled={disabled}
                    onDeletePhone={(phoneToDelete) => {
                      setProperty('phones', leadershipUpdatedInfo.address.phones.filter(p => p.id !== phoneToDelete.id))
                    }}
                    onUpdatePhone={(updatedPhone) => {
                      setProperty('phones', leadershipUpdatedInfo.address.phones.map(p =>
                        p.id === updatedPhone.id ? updatedPhone : p
                      ))
                    }}
                    address={leadershipUpdatedInfo.address}
                  />
                ))}
              </Box>
            </Box>
          </Grid>
        <Grid item xs={12}>
        <Box sx={{ mt: 4 }}>
          {leadershipUpdatedInfo.urls.map((url) => (
            <UrlCardItem
              key={url.id}
              originalUrl={url}
              onSave={(updatedUrl) => {
                setProperty('urls', leadershipUpdatedInfo.urls.map(u =>
                  u.id === updatedUrl.id ? updatedUrl : u
                ))
              }}
              onDelete={(urlToDelete) => {
                setProperty('urls', leadershipUpdatedInfo.urls.filter(u =>
                  u.id !== urlToDelete.id
                ))
              }}
              updateOriginal={(url) => {
                setProperty('urls', leadershipUpdatedInfo.urls.map(u =>
                  u.id === url.id ? url : u
                ))
              }}
              startDisabled={disabled}
              roleIds={[leadershipUpdatedInfo.role.id]}
              showButtons={false}
            />
          ))}
          {!disabled && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <IconButtonWithTooltip
                label="Add URL"
                color="secondary"
                onClick={() => {
                  const newUrl = getEmptyLeadershipUrl(
                    leadershipUpdatedInfo.id,
                    leadershipUpdatedInfo.urls.length + 1,
                    true
                  )
                  newUrl.id = -Date.now() // Override the default -1 with timestamp for uniqueness
                  setProperty('urls', [...leadershipUpdatedInfo.urls, newUrl])
                }}
                icon={<AddIcon />}
              />
            </Box>
          )}
        </Box>
        </Grid>
          <Grid item xs={6}>
            <ResponsibilityMultiSelect disabled={disabled} value={leadershipUpdatedInfo.responsibilities} onChange={(selectedIds) => { setProperty('responsibilities', selectedIds) }}/>
          </Grid>
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Box sx={{ display: 'flex', justifyContent: 'end' }}>
          {loadingClick && <CircularProgress/>}
          {disabled && (
            <CommentDialog recordId={leadershipOriginalInfo.id} recordType={getLeadershipCommentRecordType(leadershipOriginalInfo.role.id)} recordStateId={leadershipOriginalInfo.state.id}/>
          )}
          <EditActions
            entityName="Leadership/Officer"
            editing={!disabled}
            saving={loadingClick}
            savePlacement="left"
            onEdit={handleStartEditing}
            onCancel={() => { setLeadershipUpdatedInfo(leadershipOriginalInfo); setDisabled(true) }}
            onSave={handlePostClick}
          />
          {disabled && (
            <IconButtonWithTooltip label={'Delete Leadership/Officer'} color="secondary" onClick={() => { setOpenDeleteDialog(true) }} disabled={loadingClick} icon={<DeleteIcon />} />
          )}
        </Box>
        </Grid>
      </Grid>
        </Card>
        {leadershipJurisdiction != null && (
          <Box sx={{ mt: 4, width: '100%' }}>
            <JurisdictionCard<LeadershipOfficeJurisdiction>
              loading={loadingClick}
              originalJurisdiction={leadershipJurisdiction}
              setOriginalJurisdiction={setLeadershipJurisdiction}
            />
          </Box>
        )}

        <AlertDialog
            onClose={() => { setShowRetireConfirmation(false) }}
            open={showRetireConfirmation}
            onButtonClick={handleRetire}
            title='Would you like to retire this Officer/leader?'
            contentText='This action cannot be undone.'
            nameButtonCancel='Close'
            nameButtonConfirm='Retire'
        />
        <AlertDialog
            onClose={() => { setOpenDeleteDialog(false) }}
            open={openDeleteDialog}
            onButtonClick={handleDelete}
            title='Would you like to delete this Leadership/Officer?'
            contentText='This action cannot be undone.'
            nameButtonCancel='Close'
            nameButtonConfirm='Delete'
        />
        <Dialog
            open={openFillDialog}
            onClose={() => { setOpenFillDialog(false) }}
            fullWidth
            maxWidth="md"
            PaperProps={{
              sx: {
                minHeight: '60vh'
              }
            }}
        >
          <DialogTitle>Select Person</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ pt: 2 }}>
              <Grid item xs={12}>
                <Grid container alignItems="center" spacing={1}>
                  <Grid item xs={12}>
                    <PersonAutocomplete<PersonBaseBasicInformation>
                        person={undefined}
                        updatePerson={(person) => { handleFill(person) }}
                        filledPerson={false}
                        editable={true}
                        editing={true}
                        setEditing={() => {}}
                        createPerson={false}
                        setCreatePerson={() => {}}
                        personType={'personUS'}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </DialogContent>
        </Dialog>
        <LeadershipOfficerDisplayOrderDialog
          open={displayOrderModalOpen}
          onClose={handleDisplayOrderModalClose}
          onDisplayOrderUpdated={handleDisplayOrderUpdated}
          chamberId={leadershipUpdatedInfo.legchamber.id}
          stateId={leadershipUpdatedInfo.state?.id}
        />
        <UnsavedChangesDialog
          open={unsavedChangesOpen}
          onSave={handleSaveAndNavigate}
          onDiscard={handleDiscardAndNavigate}
          onCancel={handleCancelNavigation}
        />
    </Box>
    </Stack>
  )
}
